/**
 * Sentry Integration for Frontend
 *
 * Initializes Sentry error tracking and performance monitoring
 * for the Synap Marketing OS frontend.
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for the frontend
 * Should be called before React renders
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('[Sentry] VITE_SENTRY_DSN not configured - error tracking disabled');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Only send errors in production, or if explicitly enabled
    enabled: import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLED === 'true',

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Capture 10% of all sessions
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Session replay sample rate
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0, // Always capture replays on errors

    // Filter out sensitive data
    beforeSend(event) {
      // Remove sensitive headers if present
      if (event.request?.headers) {
        delete event.request.headers['authorization'];
        delete event.request.headers['cookie'];
        delete event.request.headers['x-api-key'];
      }

      // Scrub sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data) {
            const data = { ...breadcrumb.data };
            delete data.password;
            delete data.token;
            delete data.apiKey;
            delete data.secret;
            return { ...breadcrumb, data };
          }
          return breadcrumb;
        });
      }

      return event;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Ignore network errors that are expected
      'Network request failed',
      'Failed to fetch',
      'NetworkError',
      'ECONNRESET',
      // Ignore cancelled requests
      'AbortError',
      'The operation was aborted',
      // Ignore extension errors
      /^chrome-extension/,
      /^moz-extension/,
      // Ignore ResizeObserver errors (common in browsers)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],

    // Deny URLs that shouldn't be tracked
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Safari extensions
      /^safari-extension:\/\//i,
    ],
  });

}

/**
 * Capture an exception with additional context
 */
export function captureException(
  error: Error,
  context?: {
    userId?: string;
    agencyId?: string;
    brandId?: string;
    tier?: string;
    action?: string;
    extra?: Record<string, unknown>;
  }
): void {
  if (context) {
    Sentry.withScope((scope) => {
      if (context.userId) scope.setUser({ id: context.userId });
      if (context.agencyId) scope.setTag('agency_id', context.agencyId);
      if (context.brandId) scope.setTag('brand_id', context.brandId);
      if (context.tier) scope.setTag('tier', context.tier);
      if (context.action) scope.setTag('action', context.action);
      if (context.extra) scope.setExtras(context.extra);

      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Set user context for all subsequent events
 */
export function setUserContext(userId: string, agencyId?: string, tier?: string): void {
  Sentry.setUser({ id: userId });
  if (agencyId) Sentry.setTag('agency_id', agencyId);
  if (tier) Sentry.setTag('tier', tier);
}

/**
 * Clear user context (e.g., on logout)
 */
export function clearUserContext(): void {
  Sentry.setUser(null);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  category: string,
  message: string,
  level: 'debug' | 'info' | 'warning' | 'error' = 'info',
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data,
  });
}

/**
 * Sentry Error Boundary component for wrapping the app
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * React hook for capturing errors in components
 */
export { Sentry };
